import { useCreateNewSimpleAssetRegister } from "hooks/createEntity";
import type { AssetPosition } from "kubb";
import type { SimpleAssetDefinition } from "kubb";
import type React from "react";
import { useMemo, useState } from "react";
import AlertBanner from "../../../../components/Banner/AlertBanner";
import { ErrorBanner } from "../../../../components/Error/ErrorBanner";
import BlockSpinner from "../../../../components/Spinners/BlockSpinner";
import { Button } from "../../../../components/Theme/button";
import { Heading } from "../../../../components/Theme/heading";
import { removeMapBlanks } from "../../../../utils/object.tsx";
import SimpleAssetRegisterUploadPreview from "./SimpleAssetRegisterUploadPreview";
import { type ParsedAssetDefinition, UploadPage } from "./UploadPage";
import { type PadConfig, applyPadToDefinition } from "./padSerialNumber";

interface Props {
  assetPosition: AssetPosition;
}

export default function SimpleAssetRegisterUploadContainer(
  props: Props,
): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [register, setRegister] = useState<ParsedAssetDefinition[]>([]);
  const [padConfig, setPadConfig] = useState<PadConfig>({
    enabled: true,
    length: 8,
  });
  const [state, setState] = useState<"upload" | "complete" | "error">("upload");

  const { assetPosition } = props;

  const create = useCreateNewSimpleAssetRegister(assetPosition);

  // Recompute on every pad-config change so the preview table updates live.
  // applyPadToDefinition writes the padded value into both `object` (the
  // value posted to the API) and `original` (what UploadPreviewTable
  // renders) — keeping the preview and the submission perfectly in sync.
  const paddedRegister = useMemo(
    () => register.map((d) => applyPadToDefinition(d, padConfig)),
    [register, padConfig],
  );

  const handleNewFile = (
    definitions: Promise<ParsedAssetDefinition[]>,
  ): void => {
    setLoading(true);
    definitions
      .then((result) => {
        setRegister(result);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpload = (): void => {
    setLoading(true);
    const validRegister = paddedRegister.filter(
      (object: ParsedAssetDefinition) => {
        return !(object.errors && object.errors.length > 0);
      },
    );

    create
      .update(
        validRegister.map(
          (o) =>
            removeMapBlanks(
              o.object as SimpleAssetDefinition,
            ) as SimpleAssetDefinition,
        ),
      )
      .then(() => {
        setState("complete");
      })
      .finally(() => setLoading(false));
  };

  const cancelUpload = (): void => {
    setRegister([]);
  };

  return (
    <>
      {state === "upload" && (
        <BlockSpinner loading={loading}>
          {register.length === 0 ? (
            <UploadPage handleFile={handleNewFile} />
          ) : (
            <>
              <SimpleAssetRegisterUploadPreview
                definitions={paddedRegister}
                padConfig={padConfig}
                setPadConfig={setPadConfig}
              />
              <Button color={"brandLight"} onClick={handleUpload}>
                Upload
              </Button>
              <Button color={"red"} onClick={cancelUpload}>
                Cancel
              </Button>
            </>
          )}
        </BlockSpinner>
      )}
      {state === "complete" && (
        <AlertBanner className={"bg-green-700"}>
          <Heading>Complete</Heading>
          <p>Your upload was successfully processed</p>
        </AlertBanner>
      )}
      {state === "error" ||
        (create.error && <ErrorBanner error={create.error} />)}
    </>
  );
}
